import { useState } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuMessagesSquare, LuSave } from 'react-icons/lu';

import type { AdvisorServiceLead } from './types';
import { type AdviserOption, AdviserSelect } from '~/components/AssignedUsers/AdviserSelect';
import { LEAD_LIST_DEFAULT_FILTERS } from '~/constants';
import { useChangeAddedAdvisorMutation } from '~/features/leadDetails/hooks/useChangeAddedAdvisorMutation';
import { useAdviserAssign } from '~/features/leadList/hooks/useAdviserAssign';

type Props = {
    advisorId: string;
    lead: AdvisorServiceLead;
    onClose: () => void;
    open: boolean;
};

const LABEL_ID = 'change-added-advisor-label';

// the added advisor hands their share to another visible user; they lose access, the owner stays
export const ChangeAddedAdvisorModal: React.FC<Props> = ({ advisorId, lead, onClose, open }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isOnLeadDetails = useRouterState({
        select: (state) => state.matches.some((match) => match.routeId.startsWith('/_app/leads_/details/')),
    });
    const { activeAdviser, advisers } = useAdviserAssign(advisorId, lead.userIds);
    const { isPending, mutateAsync } = useChangeAddedAdvisorMutation(lead.id);
    const [selectedId, setSelectedId] = useState<string>();
    const selectable = advisers.filter(({ id }) => !lead.userIds.includes(id));
    const selected = selectable.find(({ id }) => id === selectedId);
    const options: AdviserOption[] = [
        { disabled: true, label: activeAdviser, value: advisorId },
        ...selectable.map(({ id, isAssignable, name }) => ({ disabled: !isAssignable, label: name, value: id })),
    ];

    const close = () => {
        setSelectedId(undefined);
        onClose();
    };

    const save = async () => {
        if (!selected) return;
        try {
            await mutateAsync(selected.id);
        } catch {
            // the mutation's toast explains the failure
            return;
        }
        close();
        // share handed on → no access anymore
        if (isOnLeadDetails) navigate({ search: LEAD_LIST_DEFAULT_FILTERS, to: '/leads' });
    };

    return (
        <Modal onClose={close} open={open} panelClassName="max-w-lg" title={t('LEADS.ADVISOR_SERVICE.CHANGE_TITLE')}>
            <div className="flex flex-col gap-6" data-testid="change-added-advisor-modal">
                <div className="grid grid-cols-[max-content_1fr] items-center gap-x-6">
                    <span className="flex items-center gap-1 text-sm text-midnight" id={LABEL_ID}>
                        <LuMessagesSquare aria-hidden className="size-4 shrink-0" />
                        {t('LEADS.ASSIGNED_USERS.CASE_WORKER')}
                    </span>
                    <AdviserSelect
                        aria-labelledby={LABEL_ID}
                        data-testid="change-added-advisor-select"
                        disabled={isPending}
                        onSelect={setSelectedId}
                        options={options}
                        placeholder={t('SELECT_PLACEHOLDER')}
                        value={selectedId ?? advisorId}
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <Button
                        disabled={isPending}
                        iconLeft={<LuCircleX />}
                        onClick={close}
                        variant={ButtonVariant.SECONDARY}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        data-testid="change-added-advisor-save"
                        disabled={!selected || isPending}
                        iconLeft={<LuSave />}
                        onClick={save}
                        variant={ButtonVariant.PRIMARY}
                    >
                        {t('BUTTONS.SAVE')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
